import { z } from "zod";
import {
  journalNewSourceSchema,
  journalPrefillText,
  journalSectionSourceSchema,
  journalStodordSchema,
  journalWebdocStatusSchema,
  type JournalPrefillLine,
  type JournalPrefillSourceRef,
} from "./journal-provenance";
import {
  isMonthlyNoteF04Field,
  MONTHLY_WEEKLY_ACTIVATION,
} from "./f04-field-class";
import {
  summaryItemText,
  type PreConsultSummary,
  type PreConsultSummaryItem,
} from "./preconsult-summary";
import {
  journalDecisionLines,
  requestedCompletionText,
  TRANSFER_OUTCOME_JOURNAL_LABEL,
  type JournalDecision,
} from "./journal-decision";
import { COMPLETION_OUTCOME_LABEL } from "./completion-picker";
import {
  CHECKIN_SYMPTOM_FIELD_PART_KEYS,
  checkinSymptomFieldKey,
  checkinSymptomFieldKeys,
  SYMPTOM_REGISTER_V2,
  type CheckinFieldPrefix,
} from "./symptom-entry";
import {
  journalLabResultLines,
  journalRiskFlagLine,
  type JournalLabResult,
  type JournalRiskFlag,
} from "./journal-evidence-lines";
import {
  isNegativeAnswer,
  journalFieldPhrase,
  journalMeasureValue,
  journalPhrasedLine,
  PER_SYMPTOM_LABEL,
  type JournalFieldPhrase,
} from "./journal-phrasing";
import type { PrescriptionEvidenceReview } from "./prescription-evidence-review";
import {
  dedupeAgainstTransfer,
  TRANSFER_PARAGRAPH_KEYS,
  transferMedicationOf,
  transferMedicationParagraph,
} from "./transfer-medication-journal";
import {
  bloodPressurePulseJournalLine,
  type BloodPressurePulseJournalReading,
} from "./measurement-review-journal";
import { measurementSourceLabel } from "./measurement-source";
import {
  meetingOrderIsEmpty,
  ordinationLine,
  ordinationProseBody,
  prescriptionLabel,
  type MeetingPrescriptionOrder,
} from "./prescription-cycle";

// TES-904 steps 1–3, shaped by TES-941 §JOURNALANTECKNINGEN — the care
// meeting's journal note as seven fixed headings, each one editable box.
//
// TES-941 is the newer decision and wins over the epic where they differ:
//   "Alla journalrubriker ska vara synliga samtidigt"
//   "Under varje rubrik ska det endast finnas en ren, redigerbar ruta"
//   "Det ska finnas en enda knapp längst ner: ”Sammanställ journal”"
// So this slice has no per-section status workflow and no verification stamp;
// a section is a heading, an order and a text.
//
// TES-1328 (TES-1327 §1–2) — "Åtgärd och behandlingsplan" stays a heading
// but is locked: its text is generated when the note is compiled
// (journalActionPlanText), and the eighth heading "Ordination" that TES-1019
// added is gone. The note is assembled in the step after "Sammanställ
// journalanteckning", not by a button of its own.

// The headings, verbatim and in order (TES-941). The key is what is stored;
// the heading is what the doctor and Webdoc read. Renaming a heading is a
// copy change here; renaming a key is a data migration.
export const JOURNAL_SECTION_KEYS = [
  "aktuellt",
  "arftlighet_sjukdomar",
  "aktuella_lakemedel",
  "livsstil",
  "status",
  "bedomning",
  "atgard_behandlingsplan",
] as const;
export const journalSectionKeySchema = z.enum(JOURNAL_SECTION_KEYS);
export type JournalSectionKey = z.infer<typeof journalSectionKeySchema>;

export const JOURNAL_SECTION_HEADINGS: Record<JournalSectionKey, string> = {
  aktuellt: "Aktuellt",
  arftlighet_sjukdomar: "Ärftlighet och sjukdomar",
  aktuella_lakemedel: "Aktuella läkemedel",
  livsstil: "Livsstil",
  status: "Status",
  bedomning: "Bedömning",
  atgard_behandlingsplan: "Åtgärd och behandlingsplan",
};

/** 1-based position of a heading in the note. */
export function journalSectionOrder(key: JournalSectionKey): number {
  return JOURNAL_SECTION_KEYS.indexOf(key) + 1;
}

// The same ceiling as the free-text meeting columns the two legacy sections
// mirror into (documentMeetingSchema's `assessment`/`treatmentPlanNote`).
export const JOURNAL_SECTION_MAX = 10000;

// The legacy columns each section inherits on first read. `Meeting.assessment`
// was the whole note in one box, labelled "Utkast till journalanteckning" but
// placeholdered "Klinisk bedömning…" and filled by the AI's "Bedömning" draft,
// so its content is the doctor's assessment. `treatmentPlanNote` was labelled
// "Behandlingsplan". Both keep being written from these sections during the
// transition, so every reader of the old columns stays correct.
export const JOURNAL_LEGACY_COLUMN: Partial<
  Record<JournalSectionKey, "assessment" | "treatmentPlanNote">
> = {
  bedomning: "assessment",
  atgard_behandlingsplan: "treatmentPlanNote",
};

export const meetingJournalSectionSchema = z.object({
  key: journalSectionKeySchema,
  heading: z.string(),
  sortOrder: z.number().int(),
  // "" when nothing is written — never null, so the admin binds it directly.
  content: z.string(),
  // Who last wrote in the box. Null = untouched by a clinician: either empty,
  // prefilled from the forms, or carried over from the legacy column.
  editedAt: z.string().nullable(),
  editedByEmployeeId: z.string().uuid().nullable(),
  // TES-978 — the prefill the box was built from, line by line with its
  // source ("Källa"), and a newer prefill waiting beside the doctor's text
  // (TES-1244: "Granska kompletteringen"). Absent on a note read before TES-978.
  source: journalSectionSourceSchema.nullable().optional(),
  newSource: journalNewSourceSchema.nullable().optional(),
  // TES-1040 — true only on an admin's support read: the box shows the
  // prefill the doctor's next read would write, computed and not stored.
  // Absent on every stored box.
  preview: z.literal(true).optional(),
});
export type MeetingJournalSection = z.infer<typeof meetingJournalSectionSchema>;

export const meetingJournalSchema = z.object({
  meetingId: z.string().uuid(),
  sections: z.array(meetingJournalSectionSchema),
  // TES-978 — the doctor's shorthand for the note, as last autosaved.
  stodord: journalStodordSchema.nullable().optional(),
  // TES-978 — which final version is marked "Införd i Webdoc", if any.
  webdoc: journalWebdocStatusSchema.optional(),
});
export type MeetingJournal = z.infer<typeof meetingJournalSchema>;

// PUT …/journal — the boxes the doctor changed. Absent keys are untouched.
export const updateMeetingJournalSchema = z.object({
  sections: z
    .array(
      z.object({
        key: journalSectionKeySchema,
        content: z.string().max(JOURNAL_SECTION_MAX),
      }),
    )
    .min(1)
    .max(JOURNAL_SECTION_KEYS.length)
    .refine((s) => new Set(s.map((x) => x.key)).size === s.length, {
      message: "duplicate section key",
    }),
});
export type UpdateMeetingJournal = z.infer<typeof updateMeetingJournalSchema>;

// The shapes the admin's shared journal card works with, whichever note it
// shows (TES-978): a section's key is the note's own, so it is a string here.
export type JournalSection = Omit<MeetingJournalSection, "key"> & {
  key: string;
};
export interface UpdateJournalSections {
  sections: { key: string; content: string }[];
}

// ---- prefill (TES-904 step 2) ---------------------------------------------

// Where a form's answers land. Keyed by the stored `templateKey` and by the
// section ids and field keys of the template the patient answered (stable
// since intake v5 and prescription_transfer v1). A FIELD entry wins over its
// section's; `null` means "never in the journal" (consents, the signature box).
// A section or field that is not listed is left out, so a new form section
// only reaches the note once someone decides where it belongs.
//
// Livsstil is read from F-01, the weekly check-in's SHARED answers and the
// monthly check-in form since TES-1019; F-02 and F-07 do not ask about it.
interface TemplateMapping {
  sections: Record<string, JournalSectionKey | null>;
  fields?: Record<string, JournalSectionKey | null>;
  // TES-1019 — a second gate after the mapping: a field this refuses is
  // never read, whatever the map says. The weekly check-in's DOCTOR/SHARED
  // classification (f04-field-class.ts) rides here, failing closed.
  allow?: (key: string) => boolean;
  // TES-1019 — an answer that only matters when she reported something: the
  // stored value listed means "nothing to report", and the line is left out
  // (the weekly activation rule, MONTHLY_WEEKLY_ACTIVATION).
  skipWhen?: Readonly<Record<string, string>>;
  // TES-1019 — a repeated form (a check-in) or an early one (F-01) is dated
  // in the line itself, so the doctor sees which week or month it speaks for.
  dated?: boolean;
}

// ---- F-05's symptoms as prose (TES-1453, Ö-735) ----------------------------
//
// Ö-735, Emelie 2026-10-19: "Journalutkastet ska direkt sammanfatta relevant
// patientunderlag till sammanhängande brödtext under rätt journalrubrik, både
// under vårdmötet och vid månadsavstämningen. Råa formulärfrågor, råsvar,
// svarstabeller eller inklistrade fråge-/svarslistor får inte fylla
// journalrubrikerna."
//
// F-05 v7 asks five questions per symptom she chose. Written one answer per
// line, a month with three symptoms is a fifteen-line answer table, so the
// care meeting (under "Aktuellt") and the monthly review (under
// "Biverkningar") write them as one paragraph instead: the gate in Ö-734's
// label, then one sentence per symptom in the register's words, then eating
// and drinking and whether she sought care. Her answers are quoted as the form
// renders them (option labels lower-cased inside the sentence), nothing is
// inferred, and an answer she declined is left out. Every answer read stays
// reachable as a source of the paragraph, with its exact question.
//
// v2–v6 asked the gate and the list too; their per-symptom questions were
// other questions (TES-1251) and are not read, as before.

export const F05_SYMPTOM_GATE_KEY = "month_symptoms";
export const F05_SYMPTOM_TYPES_KEY = "month_symptom_types";
export const F05_SYMPTOM_CARE_KEY = "month_symptom_care";
export const F05_SYMPTOM_JOURNAL_FIELDS: readonly string[] = [
  F05_SYMPTOM_GATE_KEY,
  F05_SYMPTOM_TYPES_KEY,
  ...checkinSymptomFieldKeys("month"),
  "month_intake",
  F05_SYMPTOM_CARE_KEY,
];

// ---- F-04's symptoms as prose (TES-1535, Ö-802) ----------------------------
//
// Ö-802, Emelie 2026-10-21: "Ja, den del av F-04:s medicinska
// besvärsunderlag som redan ska ingå i ett redigerbart journalutkast ska
// sammanfattas som begriplig löptext i stället för inklistrade råa frågor och
// svar, enligt gemensamma journalprincipen. Detta ger inte läkaren tillgång
// till privata coachfält/anteckningar och skapar ingen automatisk
// journalpublicering. Originalsvar kan öppnas som källa."
//
// So the weekly check-in's complaint answers — exactly the ones the note
// already read (DOCTOR fields, f04-field-class.ts) — are written the way
// F-05's are: the gate, one sentence per symptom (v7) or the list and its
// v1–v6 follow-ups, then the two safety questions, and v1/v2's side-effect
// pair. Adherence and drive are not symptom answers and keep their lines.
export const F04_SYMPTOM_GATE_KEY = "week_new_or_changed_symptoms";
export const F04_SYMPTOM_JOURNAL_FIELDS: readonly string[] = [
  F04_SYMPTOM_GATE_KEY,
  "week_symptom_types",
  // v1–v6: asked once for all symptoms.
  "week_symptom_timing",
  "week_symptom_severity",
  // v7 (TES-1251): the same questions per symptom.
  ...checkinSymptomFieldKeys("week"),
  "week_symptom_impact",
  "week_urgent_screen",
  // v1/v2
  "sideEffects",
  "sideEffectSeverity",
];

// Ö-801, Emelie 2026-10-21: "Ta med nekande vårdkontaktsvar kort i besvärens
// journalutkast: ”Patienten uppger att hon inte har sökt vård för
// besvären.” Återge som patientuppgift utan medicinsk slutsats; läkaren
// redigerar/granskar före journalföring. Originalsvar och källa bevaras."
// Her "Nej" on "Har du sökt vård för besvären?" is written as this sentence,
// an exception to TES-1050's plain-"no" rule for this field only. Only F-05
// asks the question; no F-04 version does.
export const NO_CARE_SOUGHT_SENTENCE =
  "Patienten uppger att hon inte har sökt vård för besvären.";

interface CheckinSymptomParagraph {
  templateKey: "weekly_checkin" | "milestone";
  prefix: CheckinFieldPrefix;
  fields: ReadonlySet<string>;
  gate: string;
  /** The list and, before v7, the questions asked once for all symptoms. */
  listed: readonly string[];
  /** After the symptoms, in the form's order. */
  after: readonly string[];
}

const F05_PARAGRAPH: CheckinSymptomParagraph = {
  templateKey: "milestone",
  prefix: "month",
  fields: new Set(F05_SYMPTOM_JOURNAL_FIELDS),
  gate: F05_SYMPTOM_GATE_KEY,
  listed: [F05_SYMPTOM_TYPES_KEY],
  after: ["month_intake", F05_SYMPTOM_CARE_KEY],
};

const F04_PARAGRAPH: CheckinSymptomParagraph = {
  templateKey: "weekly_checkin",
  prefix: "week",
  fields: new Set(F04_SYMPTOM_JOURNAL_FIELDS),
  gate: F04_SYMPTOM_GATE_KEY,
  listed: [
    "week_symptom_types",
    "week_symptom_timing",
    "week_symptom_severity",
  ],
  after: [
    "week_symptom_impact",
    "week_urgent_screen",
    "sideEffects",
    "sideEffectSeverity",
  ],
};

// The forms whose symptom answers are written as one paragraph.
const SYMPTOM_PARAGRAPHS: Readonly<Record<string, CheckinSymptomParagraph>> = {
  milestone: F05_PARAGRAPH,
  weekly_checkin: F04_PARAGRAPH,
};

export interface F05SymptomAnswer {
  key: string;
  /** The question as her version asked it. */
  label: string;
  /** Her answer as the form renders it. */
  answer: string;
}
export type CheckinSymptomAnswer = F05SymptomAnswer;

// "Måttligt" → "måttligt" inside a sentence; a date or her own words are
// left as they are.
function inSentence(answer: string): string {
  return /^\p{Lu}\p{Ll}/u.test(answer)
    ? answer[0]!.toLowerCase() + answer.slice(1)
    : answer;
}

function symptomSentences(
  form: CheckinSymptomParagraph,
  answers: readonly CheckinSymptomAnswer[],
  templateVersion?: number | null,
): string[] {
  const given = new Map(
    answers
      .filter((a) => form.fields.has(a.key) && !isDeclinedAnswer(a.answer))
      .map((a) => [a.key, a] as const),
  );
  const phrased = (key: string): string | null => {
    const a = given.get(key);
    if (!a) return null;
    const answer = a.answer.trim();
    // Ö-801 — the sentence without its period: the paragraph closes it.
    if (F05_SYMPTOM_CARE_KEY === key && NEGATIVE_LABEL.test(answer)) {
      return NO_CARE_SOUGHT_SENTENCE.slice(0, -1);
    }
    const phrase = journalFieldPhrase(
      form.templateKey,
      key,
      templateVersion,
      a.label,
    );
    if (!phrase) return null;
    if (!phrase.keepNegative && NEGATIVE_LABEL.test(answer)) return null;
    return journalPhrasedLine(phrase.label, answer);
  };

  const sentences: string[] = [];
  const gate = phrased(form.gate);
  if (gate) sentences.push(gate);

  let perSymptom = false;
  for (const s of SYMPTOM_REGISTER_V2) {
    const parts = CHECKIN_SYMPTOM_FIELD_PART_KEYS.flatMap((part) => {
      const a = given.get(checkinSymptomFieldKey(form.prefix, s.code, part));
      if (!a) return [];
      const value = a.answer.trim();
      if (part === "note") return [`${PER_SYMPTOM_LABEL.note} ”${value}”`];
      if (part === "started") return [`${PER_SYMPTOM_LABEL.started} ${value}`];
      return [`${PER_SYMPTOM_LABEL[part]} ${inSentence(value)}`];
    });
    if (parts.length === 0) continue;
    perSymptom = true;
    sentences.push(`${s.label.sv}: ${parts.join(", ")}`);
  }
  // Before v7, or a v7 answer with no detail: the list as she chose it.
  if (!perSymptom) {
    for (const key of form.listed) {
      const line = phrased(key);
      if (line) sentences.push(line);
    }
  }

  for (const key of form.after) {
    const line = phrased(key);
    if (line) sentences.push(line);
  }
  return sentences;
}

/**
 * Her F-05 symptom answers as sentences, without the closing period, or []
 * when there is nothing to write. `templateVersion` picks the gate's label
 * (Ö-734's for v7). A plain "no" is written only for the gate, which keeps
 * it as a medically relevant negation (journal-phrasing.ts), and for sought
 * care, as Ö-801's sentence.
 */
export function f05SymptomSentences(
  answers: readonly CheckinSymptomAnswer[],
  templateVersion?: number | null,
): string[] {
  return symptomSentences(F05_PARAGRAPH, answers, templateVersion);
}

/**
 * TES-1535 (Ö-802) — her F-04 symptom answers as sentences, without the
 * closing period, or [] when there is nothing to write. Any F-04 version:
 * the gate in the label of the question her version asked, then per symptom
 * (v7) or the list with v1–v6's timing and severity, then eating and
 * drinking and the "right now" screen, and v1/v2's side effects.
 */
export function f04SymptomSentences(
  answers: readonly CheckinSymptomAnswer[],
  templateVersion?: number | null,
): string[] {
  return symptomSentences(F04_PARAGRAPH, answers, templateVersion);
}

export const JOURNAL_PREFILL_MAP: Record<string, TemplateMapping> = {
  // F-02 / F-02B — "Medicinsk hälsodeklaration" (one template, two variants).
  intake: {
    sections: {
      // The goal and the recent change are the history; the measured weight
      // and height are the findings (below).
      baseline_goals: "aktuellt",
      weight_history: "arftlighet_sjukdomar",
      medical_safety: "arftlighet_sjukdomar",
      reproductive: "aktuellt",
      mental_eating: "arftlighet_sjukdomar",
      medications: "aktuella_lakemedel",
      current_symptoms: "aktuellt",
      // intake v5–v7 only; v8 moved these questions into current_symptoms.
      labs_documents: "aktuellt",
      before_meeting: "aktuellt",
      consents: null,
    },
    fields: {
      med_current_weight: "status",
      med_height: "status",
      // "Jag bekräftar att svaren är …" — a signature, not a finding.
      med_confirmation: null,
    },
  },
  // F-07 — "Läkemedel och behandlingsunderlag".
  prescription_transfer: {
    sections: {
      current_treatment: "aktuella_lakemedel",
      // TES-1099 — F-07 v12 split the start date, twelve months, pause, own
      // dose change and side effects into a section of their own; they keep
      // the place they had under current medication.
      treatment_history: "aktuella_lakemedel",
      prescription_evidence: "aktuella_lakemedel",
      prior_labs: "status",
      blood_pressure_pulse: "status",
      // Her attestation, and from v12 her subscription choice (TES-1095
      // rule 8: no subscription data in the journal).
      confirmation: null,
    },
  },
  // TES-1019 (Emelie, point 1) — F-01, "Inledande frågor" (the BMI quiz). One
  // flat section; every field is placed explicitly, and an unlisted one is
  // left out. How she found us and how fast she wants to go are not medical.
  // Keys from both quiz generations (v1, and v2 onwards).
  f01: {
    sections: { f01: null },
    fields: {
      weightKg: "status",
      heightCm: "status",
      reproductiveStatus: "aktuellt",
      // TES-1164 — F-02 placed the same questions under medical safety,
      // i.e. heredity and disease history.
      type1Diabetes: "arftlighet_sjukdomar",
      mtcMen2: "arftlighet_sjukdomar",
      impactDegree: "aktuellt",
      desiredOutcome: "aktuellt",
      desiredOutcomeOther: "aktuellt",
      whyLoseWeight: "aktuellt",
      impactOnLife: "aktuellt",
      howLongTried: "arftlighet_sjukdomar",
      changeContext: "arftlighet_sjukdomar",
      methodsTried: "arftlighet_sjukdomar",
      weightMedHistory: "arftlighet_sjukdomar",
      knownMedications: "aktuella_lakemedel",
      bodySignals: "livsstil",
      biggestObstacles: "livsstil",
      hardestPart: "livsstil",
      emotionalEating: "livsstil",
      lifeStage: "livsstil",
      // TES-1658 — v13's new questions, placed as the ones they replace.
      hormonalLifePhase: "livsstil",
      weightChangeLifePhase: "arftlighet_sjukdomar",
      weightDifficulties: "livsstil",
      activityLevel: "livsstil",
      speedPreference: null,
      howFoundUs: null,
      howFoundUsOther: null,
    },
    dated: true,
  },
  // TES-1019 — the latest weekly check-in (F-04). Only DOCTOR and SHARED
  // answers (TES-984's classification, Ö-266), never the coach's part; the
  // private weekly reflection (TES-987) is another table and never read here.
  // Complaints and drive are the history, adherence belongs with the
  // medicines, weight is a finding, wellbeing and focus are lifestyle.
  weekly_checkin: {
    sections: {},
    fields: {
      week_new_or_changed_symptoms: "aktuellt",
      week_symptom_types: "aktuellt",
      week_symptom_timing: "aktuellt",
      week_symptom_severity: "aktuellt",
      week_symptom_impact: "aktuellt",
      week_urgent_screen: "aktuellt",
      // v7 (TES-1251) — the per-symptom questions, with the complaints.
      ...Object.fromEntries(
        checkinSymptomFieldKeys("week").map((key) => [
          key,
          "aktuellt" as const,
        ]),
      ),
      week_drive_mood_signal: "aktuellt",
      week_drive_what: "aktuellt",
      week_drive_since: "aktuellt",
      week_drive_impact: "aktuellt",
      week_dose_adherence: "aktuella_lakemedel",
      week_dose_adherence_what: "aktuella_lakemedel",
      week_weight: "status",
      week_wellbeing: "livsstil",
      week_focus: "livsstil",
      sideEffects: "aktuellt",
      sideEffectSeverity: "aktuellt",
    },
    allow: isMonthlyNoteF04Field,
    skipWhen: MONTHLY_WEEKLY_ACTIVATION,
    dated: true,
  },
  // TES-1019 — the latest monthly check-in form (F-05, `milestone`): the
  // answers the monthly note reads (TES-984), lifestyle to "Livsstil" and the
  // drive question to the history.
  milestone: {
    sections: {},
    fields: {
      // TES-1251 (Ö-444) — F-04's wellbeing question, asked in F-05 the week
      // it replaces F-04; lifestyle, as it is for F-04.
      week_wellbeing: "livsstil",
      month_hunger: "livsstil",
      month_satiety: "livsstil",
      month_cravings: "livsstil",
      month_daily_function: "livsstil",
      month_drive_mood_change: "aktuellt",
      // TES-1453 (Ö-735) — her symptoms, with the weekly complaints; written
      // as one paragraph (f05SymptomSentences), not per answer.
      ...Object.fromEntries(
        F05_SYMPTOM_JOURNAL_FIELDS.map((key) => [key, "aktuellt" as const]),
      ),
    },
    dated: true,
  },
};

// TES-1050 (Emelie, Ö-278 b, replacing assumption (a)) — an answer is written
// in the box as a fixed phrasing from JOURNAL_FIELD_PHRASING
// (journal-phrasing.ts), never as "Fråga? Svar"; everything else stays behind
// "Källa". A declined answer ("Vill inte ange") is not a finding and is never
// written. A "Nej" or "Inga" is written only where the field's entry keeps it
// as a medically relevant negation.
const DECLINED_ANSWER =
  /^(vill inte (ange|svara)|jag vill inte (ange|svara)|föredrar att inte svara|avstår)\.?$/i;

export function isDeclinedAnswer(answer: string): boolean {
  const a = answer.trim();
  return a === "" || DECLINED_ANSWER.test(a);
}

function dayOf(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10) : null;
}

export interface JournalPrefillSource {
  templateKey: string;
  summary: PreConsultSummary;
  // TES-978 — where the answers came from, for each line's source. Optional
  // so a caller without them still gets the text.
  submissionId?: string | null;
  submittedAt?: string | null;
  // The form's readable name ("Medicinsk hälsodeklaration – nybehandling").
  formLabel?: string | null;
  // TES-1019 — the version she answered (shown under the source), and her
  // stored answers, for the rules that read a value rather than its label.
  templateVersion?: number | null;
  values?: Readonly<Record<string, unknown>>;
}

// Only what the forms say. The current order is read, never written: the
// journal must not create or change a cycle (TES-904 conflict 2).
export interface JournalPrefillCycle {
  id?: string | null;
  medication: string | null;
  dose: string;
  frequency: string | null;
  duration: string | null;
  prescribedAt?: string | null;
}

export function journalAnswerLine(label: string, value: string): string {
  const l = label.trim().replace(/:$/, "");
  // A question reads as "Fråga? Svar"; a noun label as "Vikt: 82 kg".
  return /[?]$/.test(l) ? `${l} ${value}` : `${l}: ${value}`;
}

// ---- the phrased lines (TES-1050) ------------------------------------------

interface PrefillEntry {
  item: PreConsultSummaryItem;
  target: JournalSectionKey;
  ref: JournalPrefillSourceRef;
  value: string;
  phrase: JournalFieldPhrase | null;
  sourceOnly: boolean;
}

// The rendered "no" answers, for an answer whose stored value is not at hand
// or was replaced by a doctor's correction.
const NEGATIVE_LABEL =
  /^(nej|inga|ingen|inget|inget av (dessa|detta|ovanstående))\.?$/i;

function isNegativeItem(
  source: JournalPrefillSource,
  item: PreConsultSummaryItem,
): boolean {
  if (NEGATIVE_LABEL.test(item.value.trim())) return true;
  return !item.correction && isNegativeAnswer(source.values?.[item.key]);
}

/**
 * One source's entries as box lines: each phrased answer as
 * "{label}: {value}", a follow-up joined to the answer that opened it, and
 * every unphrased answer as a "Källa"-only line under its heading.
 */
function pushPhrasedLines(
  lines: Record<JournalSectionKey, JournalPrefillLine[]>,
  source: JournalPrefillSource,
  mapping: TemplateMapping,
  entries: readonly PrefillEntry[],
  undated: boolean,
): void {
  const written = (e: PrefillEntry | undefined): boolean =>
    e !== undefined && !e.sourceOnly && e.phrase !== null;
  const byKey = new Map(entries.map((e) => [e.item.key, e]));
  const day = dayOf(source.submittedAt);
  const formLabel = source.formLabel ?? source.templateKey;
  for (const e of entries) {
    if (!written(e)) {
      lines[e.target].push({ text: "", source: e.ref, sourceOnly: true });
      continue;
    }
    const phrase = e.phrase!;
    const parent = phrase.detailOf ? byKey.get(phrase.detailOf) : undefined;
    // Joined to its parent's line, which is written under the same heading.
    if (written(parent) && parent!.target === e.target) continue;
    const details = entries.filter(
      (d) =>
        written(d) &&
        d.phrase!.detailOf === e.item.key &&
        d.target === e.target,
    );
    let value = phrase.measure
      ? journalMeasureValue(e.value, phrase.measure.unit)
      : e.value;
    if (details.length > 0) {
      const detailValues = details.map((d) => d.value);
      value = details.some((d) => d.phrase!.replacesParent)
        ? detailValues.join(" – ")
        : [value, ...detailValues].join(" – ");
    }
    let text = journalPhrasedLine(phrase.label, value);
    if (day && mapping.dated && !undated)
      text = `${text} (${formLabel} ${day})`;
    else if (day && phrase.measure) text = `${text} (${day})`;
    lines[e.target].push({
      text,
      source: e.ref,
      ...(details.length > 0
        ? { sources: [e.ref, ...details.map((d) => d.ref)] }
        : {}),
    });
  }
}

// TES-1056 — F-07's blood pressure section, which the reading line below
// replaces: her F-07 answers are stored as a measurement occasion
// (FormsService.recordTransferReading), so the reading carries the same
// values plus the date, source and the doctor's assessment.
const F07_BLOOD_PRESSURE_SECTION = "blood_pressure_pulse";

/**
 * The seven boxes as the forms would fill them, each line with its source
 * (TES-978). A heading with nothing mapped to it is absent from the result —
 * the caller leaves that box empty. Pure: the API decides WHEN to apply it
 * (only to a box nobody has written).
 *
 * TES-1056 (TES-1054 point F) — `bloodPressurePulse` is the newest standing
 * blood pressure / pulse occasion. It becomes ONE Status line with the value,
 * the measurement date, the source and the doctor's assessment
 * (bloodPressurePulseJournalLine), and F-07's raw blood pressure answers are
 * then left out rather than repeated beside it.
 */
export function prefillJournalSectionLines(input: {
  sources: readonly JournalPrefillSource[];
  cycle?: JournalPrefillCycle | null;
  // TES-1057 (Emelie, point G) — set only by the prescription transfer's
  // note: the review of each prescription document she sent (empty when she
  // sent none). Its presence turns "Aktuella läkemedel" into one paragraph
  // (transfer-medication-journal.ts) and takes the F-07 medicine out of the
  // F-01 and F-02B medicine answers.
  transferEvidence?: readonly PrescriptionEvidenceReview[];
  bloodPressurePulse?:
    | (BloodPressurePulseJournalReading & { sessionId?: string | null })
    | null;
  // TES-1058 (Emelie, point B) — the medical risk flags with their outcome,
  // under "Aktuellt", and her lab results, under "Status" after the reading.
  riskFlags?: readonly JournalRiskFlag[];
  labResults?: readonly JournalLabResult[];
  // TES-1050 — leave out the "(form day)" a repeated or early form's line
  // carries, for a caller that heads the group with the form and day once
  // (the monthly note's earlier information). A measurement keeps its day.
  undated?: boolean;
}): Partial<Record<JournalSectionKey, JournalPrefillLine[]>> {
  const reading = input.bloodPressurePulse ?? null;
  const readingLine = reading ? bloodPressurePulseJournalLine(reading) : null;
  const lines: Record<JournalSectionKey, JournalPrefillLine[]> = {
    aktuellt: [],
    arftlighet_sjukdomar: [],
    aktuella_lakemedel: [],
    livsstil: [],
    status: [],
    bedomning: [],
    atgard_behandlingsplan: [],
  };

  // The order at Ella first: it is the fact, the forms are her account of it.
  if (input.cycle && input.cycle.dose.trim()) {
    const label = prescriptionLabel(input.cycle);
    lines.aktuella_lakemedel.push({
      text: `Ordination hos Ella: ${label}`,
      source: {
        kind: "prescription_cycle",
        label: "Ordination hos Ella",
        templateKey: null,
        sourceId: input.cycle.id ?? null,
        question: null,
        answer: label,
        date: input.cycle.prescribedAt ?? null,
      },
    });
  }

  const transfer =
    input.transferEvidence !== undefined
      ? transferMedicationOf(input.sources)
      : undefined;

  for (const source of input.sources) {
    const mapping = JOURNAL_PREFILL_MAP[source.templateKey];
    if (!mapping) continue;
    const entries: PrefillEntry[] = [];
    // TES-1453, TES-1535 — F-05's and F-04's symptom answers, written below
    // as one paragraph.
    const paragraph = SYMPTOM_PARAGRAPHS[source.templateKey];
    const symptoms: {
      answer: CheckinSymptomAnswer;
      ref: JournalPrefillSourceRef;
    }[] = [];
    for (const section of source.summary.sections) {
      if (
        readingLine &&
        source.templateKey === "prescription_transfer" &&
        section.id === F07_BLOOD_PRESSURE_SECTION
      ) {
        continue;
      }
      for (const item of section.items) {
        const target =
          mapping.fields && item.key in mapping.fields
            ? mapping.fields[item.key]
            : mapping.sections[section.id];
        if (!target) continue;
        if (mapping.allow && !mapping.allow(item.key)) continue;
        const answer = summaryItemText(item);
        // The value the line states; the source below keeps her original.
        let value = answer;
        if (transfer !== undefined) {
          // TES-1057 — said once, in the medication paragraph.
          if (
            source.templateKey === "prescription_transfer" &&
            TRANSFER_PARAGRAPH_KEYS.has(item.key)
          ) {
            continue;
          }
          const deduped = dedupeAgainstTransfer(
            source,
            item,
            transfer?.medication ?? null,
          );
          if (deduped === null) continue;
          if (deduped !== undefined) value = deduped;
        }
        const ref: JournalPrefillSourceRef = {
          kind: "form",
          label: source.formLabel ?? source.templateKey,
          templateKey: source.templateKey,
          sourceId: source.submissionId ?? null,
          question: item.label,
          answer,
          date: source.submittedAt ?? null,
          ...(source.templateVersion != null
            ? { templateVersion: source.templateVersion }
            : {}),
        };
        if (paragraph?.fields.has(item.key)) {
          symptoms.push({
            answer: { key: item.key, label: item.label, answer },
            ref,
          });
          continue;
        }
        const phrase = journalFieldPhrase(
          source.templateKey,
          item.key,
          source.templateVersion,
          item.label,
        );
        const skip = mapping.skipWhen?.[item.key];
        const quiet =
          skip !== undefined &&
          String(source.values?.[item.key] ?? "").toLowerCase() === skip;
        // TES-1050 — written in the box only through a phrasing entry; a
        // declined answer, a "nothing to report" and a "no" the entry does
        // not keep stay behind "Källa".
        const sourceOnly =
          !phrase ||
          quiet ||
          isDeclinedAnswer(item.value) ||
          (!phrase.keepNegative && isNegativeItem(source, item));
        entries.push({ item, target, ref, value, phrase, sourceOnly });
      }
    }
    pushPhrasedLines(lines, source, mapping, entries, input.undated === true);
    if (paragraph && symptoms.length > 0) {
      const sentences = symptomSentences(
        paragraph,
        symptoms.map((s) => s.answer),
        source.templateVersion,
      );
      const refs = symptoms.map((s) => s.ref);
      const day = dayOf(source.submittedAt);
      const when =
        day && !input.undated
          ? ` (${source.formLabel ?? source.templateKey} ${day})`
          : "";
      lines.aktuellt.push(
        sentences.length > 0
          ? {
              text: `${sentences.join(". ")}${when}.`,
              source: refs[0]!,
              sources: refs,
            }
          : // Nothing to write (only a "no" the entries do not keep): her
            // answers stay behind "Källa", as any unwritten answer does.
            { text: "", source: refs[0]!, sources: refs, sourceOnly: true },
      );
    }
  }

  // TES-1057 — the transfer's medication paragraph leads the box (after the
  // order at Ella, which a transfer does not have yet).
  if (transfer !== undefined) {
    const paragraph = transferMedicationParagraph(
      transfer,
      input.transferEvidence ?? [],
    );
    if (paragraph) {
      const at = lines.aktuella_lakemedel.findIndex(
        (l) => l.source?.kind !== "prescription_cycle",
      );
      lines.aktuella_lakemedel.splice(
        at === -1 ? lines.aktuella_lakemedel.length : at,
        0,
        paragraph,
      );
    }
  }

  if (input.riskFlags?.length) {
    lines.aktuellt.push(...input.riskFlags.map(journalRiskFlagLine));
  }

  if (reading && readingLine) {
    lines.status.push({
      text: readingLine,
      source: {
        kind: "measurement",
        label: measurementSourceLabel(reading),
        templateKey: null,
        sourceId: reading.sessionId ?? null,
        question: null,
        answer: readingLine,
        date: reading.measuredAt,
      },
    });
  }

  if (input.labResults?.length) {
    lines.status.push(...journalLabResultLines(input.labResults));
  }

  const out: Partial<Record<JournalSectionKey, JournalPrefillLine[]>> = {};
  for (const key of JOURNAL_SECTION_KEYS) {
    if (lines[key].length > 0) out[key] = lines[key];
  }
  return out;
}

/** The seven boxes' prefilled text (prefillJournalSectionLines, as text). */
export function prefillJournalSections(
  input: Parameters<typeof prefillJournalSectionLines>[0],
): Partial<Record<JournalSectionKey, string>> {
  const out: Partial<Record<JournalSectionKey, string>> = {};
  for (const [key, lines] of Object.entries(
    prefillJournalSectionLines(input),
  ) as [JournalSectionKey, JournalPrefillLine[]][]) {
    const text = journalPrefillText(lines);
    if (text) out[key] = text;
  }
  return out;
}

// ---- "Sammanställ journal" (TES-904 step 3) --------------------------------

// TES-1185 — "behandlingsplan", the planned medication's last part, as its
// own line under the order. Proposed wording.
function withPlanLine(
  text: string | null,
  order: MeetingPrescriptionOrder,
): string | null {
  const plan = order.plan?.trim();
  if (!plan) return text;
  const line = `Behandlingsplan: ${/[.!?]$/.test(plan) ? plan : `${plan}.`}`;
  return text ? `${text}\n${line}` : line;
}

/**
 * TES-956 — the structured order as the first line of a plan box, so the
 * doctor does not type the ordination a second time. The monthly review's
 * "Åtgärd och beslut" still takes it this way; the care meeting's note
 * writes it as planned medication in its generated section (TES-1328).
 * TES-941:
 * "Kirill ska inte skriva samma ordination i både receptfältet och behandlingsplanen."
 * Only in the assembled text: the box itself is his and
 * is never written to. Left out when the box already carries the same line
 * (he pasted it, or assembled once and saved the result), so it never appears
 * twice.
 */
function meetingOrdinationLine(
  order: MeetingPrescriptionOrder | null | undefined,
): string | null {
  if (!order || meetingOrderIsEmpty(order)) return null;
  return ordinationLine({
    medication: order.medication || null,
    dose: order.dose,
    frequency: order.frequency || null,
    duration: order.duration || null,
  });
}

function withOrdinationLine(
  text: string,
  order: MeetingPrescriptionOrder | null | undefined,
): string {
  return withLeadLine(text, meetingOrdinationLine(order));
}

// TES-978 — the care meeting and the monthly review are two notes with the
// same box model and the same "Sammanställ journal", but different headings.
// A layout is what tells them apart: the headings in order, and where the
// on-screen order is written when assembling.
export interface JournalLayout<K extends string = string> {
  keys: readonly K[];
  headings: Readonly<Record<K, string>>;
  /**
   * The plan box the order's "Ordination:" line leads when assembling (the
   * monthly review's "Åtgärd och beslut"). Absent for a note whose plan is
   * generated (`generatedSection`).
   */
  orderSection?: K;
  /**
   * TES-1328 — the locked box whose text is generated on compile from the
   * order, the decision's plan lines and the monthly follow-up
   * (journalActionPlanText): the care meeting's and the transfer review's
   * "Åtgärd och behandlingsplan". Whatever is stored in it is never
   * assembled, and the admin shows no box to write in.
   */
  generatedSection?: K;
  /**
   * TES-978 — the box the follow-up decision's line leads when assembling
   * ("Nästa uppföljning"). Only the monthly review decides one.
   */
  followUpSection?: K;
  /**
   * TES-1058 — the boxes the structured decision's lines lead when
   * assembling (journalDecisionLines): the outcome, the preliminary
   * sentence and the motivation lead `assessmentSection`; the requested
   * kompletteringar, the prescription status and the care path lead
   * `planSection` (after the order's line, where the layout has one).
   */
  assessmentSection?: K;
  planSection?: K;
  /**
   * TES-984 — boxes whose absence of basis is itself stated: an empty one is
   * assembled as JOURNAL_MISSING_MARKER under its heading instead of being
   * left out. Only the monthly note's "Livsstil" and "Psykisk status och
   * beteende" (Emelie, Ö-265/Ö-267).
   */
  missingMarkerSections?: readonly K[];
}

// Emelie 2026-09-28 (Ö-265, Ö-267): with no relevant basis the heading is
// empty and marked "saknas/ej bedömd" — never a normal finding. It says that
// nothing was reported or assessed, which is true, and nothing more.
export const JOURNAL_MISSING_MARKER = "Saknas/ej bedömd";

// The options every assembly takes: what is decided on screen but not typed
// into a box.
export interface JournalAssemblyOptions {
  order?: MeetingPrescriptionOrder | null;
  /** TES-978 — the follow-up decision as one line (nextFollowUpLine). */
  followUp?: string | null;
  /**
   * TES-1058 — the structured decision on screen (journal-decision.ts).
   * TES-1061 — or several, in order: the care meeting's own decision and
   * the later treatment decision, when both are journaled in the meeting's
   * note.
   */
  decision?: JournalDecision | null | readonly (JournalDecision | null)[];
}

function decidedLines(decision: JournalAssemblyOptions["decision"]): {
  assessment: string[];
  plan: string[];
} {
  const list = (Array.isArray(decision) ? decision : [decision]) as readonly (
    | JournalDecision
    | null
    | undefined
  )[];
  const out = { assessment: [] as string[], plan: [] as string[] };
  for (const d of list) {
    const lines = journalDecisionLines(d);
    out.assessment.push(...lines.assessment);
    out.plan.push(...lines.plan);
  }
  return out;
}

// A decided line leads its box in the assembled text, unless the box already
// carries it (pasted, or assembled and saved before).
// TES-1058 — several decided lines lead a box in their order; each one the
// box already carries is left out.
function withLeadLines(text: string, lines: readonly string[]): string {
  const have = new Set(text.split("\n").map((x) => x.trim()));
  const lead = lines.map((l) => l.trim()).filter((l) => l && !have.has(l));
  if (lead.length === 0) return text;
  return text ? `${lead.join("\n")}\n${text}` : lead.join("\n");
}

function withLeadLine(text: string, line: string | null | undefined): string {
  const l = line?.trim();
  if (!l) return text;
  if (text.split("\n").some((x) => x.trim() === l)) return text;
  return text ? `${l}\n${text}` : l;
}

// TES-1328 — the seven headings; "Åtgärd och behandlingsplan" is generated.
export const MEETING_JOURNAL_LAYOUT: JournalLayout<JournalSectionKey> = {
  keys: JOURNAL_SECTION_KEYS,
  headings: JOURNAL_SECTION_HEADINGS,
  assessmentSection: "bedomning",
  generatedSection: "atgard_behandlingsplan",
};

/**
 * One text for Webdoc: each heading on its own line, its text under it, a
 * blank line between sections. Empty sections are LEFT OUT rather than marked:
 * whatever is copied becomes the record in Webdoc, and a placeholder such as
 * "Ej bedömt" would put a clinical statement there that nobody made. The
 * admin names the skipped headings beside the button instead. The exception
 * is the layout's `missingMarkerSections`, where Emelie asked for the marker
 * (TES-984): those read JOURNAL_MISSING_MARKER when empty.
 *
 * `order` is the order on screen (TES-956): the lead line of the layout's
 * `orderSection` (withOrdinationLine), or part of its generated section
 * (TES-1328), which is left out when nothing generates it.
 */
export function assembleJournalTextFor<K extends string>(
  layout: JournalLayout<K>,
  sections: readonly { key: K; content: string }[],
  opts: JournalAssemblyOptions = {},
): string {
  const byKey = new Map<string, string>(
    sections.map((s) => [s.key, s.content]),
  );
  const blocks: string[] = [];
  const decided = decidedLines(opts.decision);
  for (const key of layout.keys) {
    let text = (byKey.get(key) ?? "")
      .replace(/\r\n?/g, "\n")
      .replace(/[ \t]+$/gm, "")
      .trim();
    if (key === layout.assessmentSection) {
      text = withLeadLines(text, decided.assessment);
    }
    if (key === layout.planSection) {
      text = withLeadLines(text, decided.plan);
    }
    if (key === layout.generatedSection) {
      // TES-1328 transition — text a doctor wrote in the box before it was
      // locked is kept, after the generated text (lines it already carries
      // are not repeated). The prefill never writes to this box, so stored
      // text is always a clinician's or the legacy column's. A note started
      // after the lock stores nothing here, so this dies out by itself.
      text = withLeadLines(
        legacyGeneratedSectionText(text),
        journalActionPlanText(opts.order, opts.decision).split("\n"),
      );
    }
    if (key === layout.orderSection) {
      text = withOrdinationLine(text, opts.order);
    }
    if (key === layout.followUpSection) {
      text = withLeadLine(text, opts.followUp);
    }
    if (!text && layout.missingMarkerSections?.includes(key)) {
      text = JOURNAL_MISSING_MARKER;
    }
    if (!text) continue;
    blocks.push(`${layout.headings[key]}\n${text}`);
  }
  return blocks.join("\n\n");
}

// TES-1185 (TES-1180 point 1) — A-02 2026-10-08, "SLUTLIG KANONISK REGEL —
// JOURNALFÖRING, PLANERAD LÄKEMEDELSBEHANDLING OCH RECEPT VID KOMPLETTERING":
// "Om kompletteringar krävs är läkemedelsuppgifterna en planerad och villkorad
// läkemedelsbehandling, inte ett utfärdat recept eller en slutligt fastställd
// ordination." Her example sentence, with the order and the requested
// kompletteringar filled in:
// "Patienten bedöms kunna vara aktuell för behandling med {läkemedel och planerad dosering}. Slutligt behandlingsbeslut och receptutfärdande inväntar bedömning av {begärda kompletteringar}."
//
// TES-1328 — otherwise the order is written as "Planerad läkemedelsbehandling"
// (A-02 2026-10-13: "Uppgifterna återanvänds automatiskt i journalen under
// Åtgärd och behandlingsplan med den patient- och journalläsbara
// formuleringen Planerad läkemedelsbehandling."), never as "Ordination".
export const PLANNED_TREATMENT_HEADING = "Planerad läkemedelsbehandling";

// TES-1328 — the hint the locked "Åtgärd och behandlingsplan" shows before
// the note is compiled, verbatim from A-02 2026-10-13.
export const JOURNAL_ACTION_PLAN_LOCKED_HINT =
  "Automatiskt sammanställt från Recept, valda kompletteringar och uppföljningsplan.";

// TES-1328 transition — the line under text a doctor wrote in the box before
// it was locked, which the note keeps after the generated text. Proposed
// wording.
export const JOURNAL_ACTION_PLAN_LEGACY_NOTICE =
  "Skrevs innan fältet låstes och tas med i journalanteckningen efter den sammanställda texten.";

/**
 * TES-1328 transition — the text stored in the locked box that the note
 * keeps: trimmed, or "" when there is none or it is only the box's prefill.
 * (The prefill map never writes to "Åtgärd och behandlingsplan", so in
 * practice any stored text is kept.)
 */
export function legacyGeneratedSectionText(
  content: string | null | undefined,
  prefill?: string | null,
): string {
  const text = (content ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .trim();
  if (!text) return "";
  if (prefill && text === prefill.trim()) return "";
  return text;
}

// TES-1328 — the standard follow-up plan, written whenever treatment is
// planned or decided. TES-1327: "När behandling planeras eller beslutas ska
// standardtexten ange att behandlingen följs upp månadsvis via patientens
// månadsincheckning." Her rule as one sentence; proposed wording.
export const JOURNAL_MONTHLY_FOLLOW_UP_LINE =
  "Behandlingen följs upp månadsvis via patientens månadsincheckning.";

// The outcomes that end the treatment path: no planned treatment and no
// follow-up is written for them, whatever order is stored.
const NO_TREATMENT_OUTCOMES: ReadonlySet<string> = new Set([
  COMPLETION_OUTCOME_LABEL.cannot_proceed,
  TRANSFER_OUTCOME_JOURNAL_LABEL.cannot_transfer ?? "",
]);

function decisionList(
  decision: JournalAssemblyOptions["decision"],
): JournalDecision[] {
  return (Array.isArray(decision) ? decision : [decision]).filter(
    (d): d is JournalDecision => Boolean(d),
  );
}

// The order as planned medication: the conditional sentence while the
// newest decision waits for kompletteringar, else "Planerad
// läkemedelsbehandling: …". Null without an order.
function plannedTreatmentText(
  order: MeetingPrescriptionOrder | null | undefined,
  newest: JournalDecision | undefined,
): { text: string; namesCompletion: boolean } | null {
  if (!order || meetingOrderIsEmpty(order)) return null;
  const body = ordinationProseBody({
    medication: order.medication || null,
    dose: order.dose,
    frequency: order.frequency || null,
    duration: order.duration || null,
    strength: order.strength || null,
  });
  if (!body) {
    const plan = withPlanLine(null, order);
    return plan ? { text: plan, namesCompletion: false } : null;
  }
  if (!newest?.pending) {
    return {
      text: withPlanLine(`${PLANNED_TREATMENT_HEADING}: ${body}.`, order) ?? "",
      namesCompletion: false,
    };
  }
  const requested = newest.completion
    ? requestedCompletionText(newest.completion)
    : null;
  const sentence = [
    `Patienten bedöms kunna vara aktuell för behandling med ${body}.`,
    requested
      ? `Slutligt behandlingsbeslut och receptutfärdande inväntar bedömning av begärda kompletteringar: ${requested}.`
      : "Slutligt behandlingsbeslut och receptutfärdande inväntar bedömning av begärda kompletteringar.",
  ].join(" ");
  return {
    text: withPlanLine(sentence, order) ?? sentence,
    namesCompletion: requested !== null,
  };
}

/**
 * TES-1328 (A-02 2026-10-13) — the text "Sammanställ journalanteckning"
 * writes under the locked "Åtgärd och behandlingsplan", from three parts in
 * this order: the Recept as planned medication, the kompletteringar the
 * doctor chose (with the rest of the decision's plan lines: prescription
 * status, care path), and the standard monthly follow-up when treatment is
 * planned or decided. "" when none of them has anything to say.
 */
export function journalActionPlanText(
  order: MeetingPrescriptionOrder | null | undefined,
  decision: JournalAssemblyOptions["decision"],
): string {
  const list = decisionList(decision);
  const newest = list[list.length - 1];
  const endsTreatment =
    newest?.outcome != null && NO_TREATMENT_OUTCOMES.has(newest.outcome);
  const planned = endsTreatment ? null : plannedTreatmentText(order, newest);
  const lines: string[] = [];
  if (planned) lines.push(planned.text);
  for (const d of list) {
    // The conditional sentence already names the newest decision's
    // kompletteringar; they are not listed a second time.
    const named = d === newest && planned?.namesCompletion;
    lines.push(
      ...journalDecisionLines(named ? { ...d, completion: null } : d).plan,
    );
  }
  if (planned) lines.push(JOURNAL_MONTHLY_FOLLOW_UP_LINE);
  return withLeadLines("", lines);
}

/** The headings "Sammanställ journal" leaves out, in note order. */
export function emptyJournalHeadingsFor<K extends string>(
  layout: JournalLayout<K>,
  sections: readonly { key: K; content: string }[],
  opts: JournalAssemblyOptions = {},
): string[] {
  const byKey = new Map<string, string>(
    sections.map((s) => [s.key, s.content]),
  );
  const hasOrder = meetingOrdinationLine(opts.order) !== null;
  const hasFollowUp = Boolean(opts.followUp?.trim());
  const decided = decidedLines(opts.decision);
  return layout.keys
    .filter((k) =>
      // TES-1328 — a generated section is empty when nothing generates it,
      // whatever is stored in it.
      k === layout.generatedSection
        ? !journalActionPlanText(opts.order, opts.decision) &&
          !legacyGeneratedSectionText(byKey.get(k))
        : !(byKey.get(k) ?? "").trim() &&
          !(k === layout.orderSection && hasOrder) &&
          !(k === layout.followUpSection && hasFollowUp) &&
          !(k === layout.assessmentSection && decided.assessment.length > 0) &&
          !(k === layout.planSection && decided.plan.length > 0) &&
          !layout.missingMarkerSections?.includes(k),
    )
    .map((k) => layout.headings[k]);
}

/** The care meeting's note as one text (MEETING_JOURNAL_LAYOUT). */
export function assembleJournalText(
  sections: readonly { key: JournalSectionKey; content: string }[],
  opts: Pick<JournalAssemblyOptions, "order" | "decision"> = {},
): string {
  return assembleJournalTextFor(MEETING_JOURNAL_LAYOUT, sections, opts);
}

/** The care meeting's empty headings (MEETING_JOURNAL_LAYOUT). */
export function emptyJournalHeadings(
  sections: readonly { key: JournalSectionKey; content: string }[],
  opts: Pick<JournalAssemblyOptions, "order" | "decision"> = {},
): string[] {
  return emptyJournalHeadingsFor(MEETING_JOURNAL_LAYOUT, sections, opts);
}

// ---- the AI drafts (TES-904 conflict 3) -----------------------------------

// The meeting-note assist still writes three texts. Each goes into exactly one
// box: the anamnesis paragraph (from the forms) is the history, the assessment
// is the assessment, the plan is the plan.
export const AI_NOTE_JOURNAL_TARGET = {
  anamnesis: "aktuellt",
  assessment: "bedomning",
  treatmentPlanNote: "atgard_behandlingsplan",
} as const satisfies Record<string, JournalSectionKey>;
